import React, { memo, useMemo } from '../../lib/teact/teact';

import type { ApiTokenWithPrice } from '../../api/types';

import { TONCOIN } from '../../config';
import getChainNetworkIcon from '../../util/swap/getChainNetworkIcon';
import { getIsNativeToken, getIsRwaStockToken } from '../../util/tokens';

import useLang from '../../hooks/useLang';
import useLastCallback from '../../hooks/useLastCallback';

import Dropdown, { type DropdownItem } from './Dropdown';

import styles from './TokenDropdown.module.scss';

import gramIcon from '../../assets/token_gram.svg';

export type TokenWithId = Pick<ApiTokenWithPrice, 'slug' | 'symbol' | 'image' | 'chain' | 'keywords'> & {
  /**
   * The token unique id to use instead of slug.
   * Made for cases when there multiple dropdown items with the same token.
   */
  id?: string;
};

interface OwnProps<T extends TokenWithId> {
  selectedToken?: T | string;
  allTokens?: T[];
  withChainIcon?: boolean;
  theme?: 'purple';
  isInMode?: boolean;
  isDisabled?: boolean;
  /** `id` is the token slug, unless an `id` property is specified explicitly in the `allTokens` items */
  onChange?: (id: string, token: T) => void;
}

const EMPTY_TOKEN_LIST: never[] = [];

function TokenDropdown<T extends TokenWithId>({
  selectedToken,
  allTokens = EMPTY_TOKEN_LIST,
  withChainIcon,
  theme,
  isInMode,
  isDisabled,
  onChange,
}: OwnProps<T>) {
  const lang = useLang();

  const buttonPrefixText = lang('$in_currency', { currency: '' });
  const buttonPrefix = useMemo(
    () => {
      return isInMode && (
        <span className={styles.prefix}>
          {buttonPrefixText}
        </span>
      );
    },
    [isInMode, buttonPrefixText],
  );

  const items = useMemo(
    () => allTokens.map((token) => tokenToDropdownItem(token, withChainIcon)),
    [allTokens, withChainIcon],
  );

  const tokenById = useMemo(
    () => allTokens.reduce<Record<string, T>>((byId, token) => {
      byId[getTokenId(token)] = token;
      return byId;
    }, {}),
    [allTokens],
  );

  const handleChange = useLastCallback((id: string) => {
    const token = tokenById[id];
    onChange?.(id, token);
  });

  if (!items.length) {
    return undefined;
  }

  const selectedTokenId = !selectedToken || typeof selectedToken === 'string'
    ? selectedToken
    : getTokenId(selectedToken);

  return (
    <Dropdown
      items={items}
      selectedValue={selectedTokenId}
      buttonPrefix={buttonPrefix}
      className={styles.dropdown}
      menuClassName={theme && styles[theme]}
      disabled={isDisabled}
      onChange={handleChange}
    />
  );
}

export default memo(TokenDropdown);

export function getTokenId(token: TokenWithId) {
  return token.id ?? token.slug;
}

export function tokenToDropdownItem(token: TokenWithId, isMultichainAccount?: boolean): DropdownItem {
  const isNativeToken = getIsNativeToken(token.slug);
  const icon = token.slug === TONCOIN.slug
    ? gramIcon
    : token.image || (isNativeToken && token.chain ? getChainNetworkIcon(token.chain) : undefined);

  return {
    value: getTokenId(token),
    icon,
    iconClassName: getIsRwaStockToken(token) ? styles.rwaStockIcon : undefined,
    overlayIcon: isMultichainAccount && !getIsNativeToken(token.slug) ? getChainNetworkIcon(token.chain) : undefined,
    name: token.symbol,
  };
}
